import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { formatNumber } from "@/lib/format";
import type { PerPage } from "@/lib/use-paged";

import { Button } from "./Button";

/** "Per page" chooser: the given sizes plus "all". */
export function PageSizeSelect({
  value,
  sizes,
  onChange,
}: {
  value: PerPage;
  sizes: readonly number[];
  onChange: (value: PerPage) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <span className="inline-flex items-center gap-2">
      <label htmlFor={id} className="text-ink-muted">
        {t("paging.perPage")}
      </label>
      <select
        id={id}
        value={String(value)}
        onChange={(e) => onChange(e.target.value === "all" ? "all" : Number(e.target.value))}
        className="h-8 rounded-control border border-line-strong bg-surface px-2 text-sm text-ink focus:border-primary focus:outline-none"
      >
        {sizes.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
        <option value="all">{t("paging.all")}</option>
      </select>
    </span>
  );
}

/** With "all": loads the next chunk when the end of the list comes into view (and offers a button too). */
export function LoadMore({
  loaded,
  total,
  hasMore,
  loading,
  onLoad,
}: {
  loaded: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  onLoad: () => void;
}) {
  const { t } = useTranslation();
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = sentinel.current;
    if (!target || !hasMore || loading || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoad();
      },
      { rootMargin: "600px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, onLoad]);

  return (
    <div ref={sentinel} className="flex flex-wrap items-center gap-3">
      <span className="text-ink-muted tabular-nums" aria-live="polite">
        {t("paging.shown", { loaded: formatNumber(loaded), total: formatNumber(total) })}
      </span>
      {hasMore && (
        <Button variant="secondary" onClick={onLoad} busy={loading}>
          {t("paging.loadMore")}
        </Button>
      )}
    </div>
  );
}
